<template lang="html">
  <div class="wrapper">
    <h2>Terminal Settings</h2>
    <p>Theme</p>
    <div class="columns">
      <div v-for="(theme, index) in themes" :key="`t${index}`">
        <input type="radio" class="radio" :id="`t${index}`" :value="index" v-model="localTheme">
        <label :for="`t${index}`">{{ theme.name }}</label><br>
      </div>
    </div>
    <transition name="slide-fade">
      <div v-if="themes[selectedTheme].name === 'Custom Theme'" class="custom-theme">
        <p>Custom theme</p>
        <input class="color" name="fgCol" type="color" v-model="localCustomPrimary">
        <label for="fgCol">Primary color</label><br>
        <input class="color" name="bgCol" type="color" v-model="localCustomSecondary">
        <label for="bgCol">Secondary color</label><br>
        <input class="color" name="bgCol" type="color" v-model="localCustomTertiary">
        <label for="bgCol">Tertiary color</label><br>
      </div>
    </transition>
    <p>Background</p>
    <div class="columns">
      <div v-for="(bg, index) in backgrounds" :key="`b${index}`">
        <input type="radio" class="radio" :id="`b${index}`" :value="index" v-model="localBg">
        <label :for="`b${index}`">{{ bg.name }}</label><br>
      </div>
    </div>
    <hr>
    <!-- //////////////////// END COSMETIC SETTINGS //////////////////// -->
    <!-- <h2>Audio Settings</h2>
    <input id="muted" type="checkbox" v-model="localMuted" style="display: inline;">
    <label for="muted">Mute audio</label><br><br>
    <label for="vol">Audio volume: {{ Math.round(localVolume) }}%</label><br>
    <input id="vol" type="range" v-model="localVolume" min="0" max="100" step="1">
    <hr> -->
    <!-- //////////////////// END AUDIO SETTINGS //////////////////// -->
    <h2>Pilot Settings</h2>
    <PilotSettings />
    <h2>C.A.R.A:P.A.C.E. Settings</h2>
    <CarapaceSettings />
    <!-- //////////////////// END PLAYER SETTINGS //////////////////// -->
    <h2>Data Settings</h2>
    <button @click="exportAsText">Export logs as .txt</button>
    <button @click="clearStorageWarningHandler">Clear save data</button>
    <div v-if="clearStorageWarning">
      <p>This action is permanent. Type DELETE to confirm.</p>
      <input type="text" v-model="confirmDelete">
      <button class="inline-block" @click="clearStorage" :disabled="confirmDelete !== 'DELETE'">Confirm</button>
      <p class="inline-block button-text" @click="clearStorageWarning = false">Cancel</p>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

import { saveAs } from 'file-saver';

import PilotSettings from '@/components/PilotSettings';
import CarapaceSettings from '@/components/CarapaceSettings';

export default {
  name: 'Settings',
  components: {
    PilotSettings,
    CarapaceSettings,
  },
  data() {
    return {
      clearStorageWarning: false,
      confirmDelete: "",
    }
  },
  computed: {
    localBg: {
      get () {
        return this.selectedBackground;
      },
      set (value) {
        this.selectBackground(value);
        this.saveState();
      }
    },
    localTheme: {
      get () {
        return this.selectedTheme;
      },
      set (value) {
        this.selectTheme(value);
        this.saveState();
      }
    },
    localCustomPrimary: {
      get () {
        return this.customTheme.vars['--primary-color'];
      },
      set (value) {
        this.updateCustomTheme({
          primary: value,
          secondary: this.localCustomSecondary,
          tertiary: this.localCustomTertiary,
        });
        this.saveState();
      }
    },
    localCustomSecondary: {
      get () {
        return this.customTheme.vars['--secondary-color'];
      },
      set (value) {
        this.updateCustomTheme({
          primary: this.localCustomPrimary,
          secondary: value,
          tertiary: this.localCustomTertiary,
        });
        this.saveState();
      }
    },
    localCustomTertiary: {
      get () {
        return this.customTheme.vars['--tertiary-color'];
      },
      set (value) {
        this.updateCustomTheme({
          primary: this.localCustomPrimary,
          secondary: this.localCustomSecondary,
          tertiary: value,
        });
        this.saveState();
      }
    },
    localVolume: {
      get () {
        return this.audio.volume * 100;
      },
      set (value) {
        this.$emit('setAudioVolume', value/100);
      }
    },
    localMuted: {
      get () {
        return this.audio.muted * 100;
      },
      set (value) {
        this.$emit('setAudioMute', value);
      }
    },
    ...mapGetters([
      'customTheme',
      'allLogsExport',
      'pilotFormatted',
      'carapaceFormatted',
      'fullDate',
    ]),
    ...mapState({
      themes: state => state.cosmetics.themes,
      backgrounds: state => state.cosmetics.backgrounds,
      selectedTheme: state => state.cosmetics.selectedTheme,
      selectedBackground: state => state.cosmetics.selectedBackground,
      currentDay: state => state.calendar.currentDay,
      audio: state => state.gameSettings.audio,
    }),
  },
  methods: {
    clearStorage() {
      localStorage.removeItem('lockdownProtocol');
      this.resetState();
      this.clearStorageWarning = false;
      this.$router.push('/');
    },
    clearStorageWarningHandler() {
      this.clearStorageWarning = true;
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          left: 0,
          behavior: 'smooth',
        });
      }, 50);
    },
    exportAsText() {
      let header = `C.A.R.A:P.A.C.E. SPLInt Output\n\n${this.pilotFormatted}\n${this.carapaceFormatted}\n\nLOCKDOWN Duration: ${this.currentDay} days\nLOCKDOWN Commenced: ${this.fullDate(0)}\n\n-------------------◆-------------------\n\n`;
      let footer = `-------------------◆-------------------\n\nEnd of output`
      let blob = new Blob([header, ...this.allLogsExport, footer], {type: "text/plain;charset=utf-8"});
      saveAs(blob, 'LOCKDOWN Protocol Export.txt');
    },
    ...mapMutations([
      'saveState',
      'selectTheme',
      'selectBackground',
      'updateCustomTheme',
      'resetState',
    ])
  },
}
</script>

<style>
.columns {
  column-count: 3;
}

.custom-theme {
  height: 142px;
  opacity: 1;
  pointer-events: all;
}

.custom-theme-hidden {
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.slide-fade-enter-active {
  transition: height 0.5s, opacity 0.5s 0.5s, margin-top 0s;
}
.slide-fade-leave-active {
  transition: opacity 0.5s, height 0.5s 0.5s, margin-top 0.5s 0.5s;
}
.slide-fade-enter, .slide-fade-leave-to {
  height: 0;
  opacity: 0;
  pointer-events: none;
  margin-top: -15px;
}

.inline-block {
  display: inline-block;
}

.button-text {
  margin-left: 20px;
}

.button-text:hover {
  cursor: pointer;
  text-decoration: underline;
}

hr {
  margin-top: 25px;
}
</style>
